import { KpiGoal, GoalProgress } from "../types/kpiGoals";
import { KpiReportResponse, MonthlyKpiStats } from "../api/kpi";
import { formatCurrency, formatInteger } from "./formatNumber";

function getMetricValue(stat: MonthlyKpiStats, key: string): number {
  return (stat as unknown as Record<string, number>)[key] ?? 0;
}

export function getCurrentValueForGoal(
  goal: KpiGoal,
  kpiData: KpiReportResponse
): number {
  const { monthlyStats } = kpiData;

  if (goal.scope === "yearly") {
    const yearPrefix = `${goal.year}-`;
    return monthlyStats
      .filter((stat) => stat.month.startsWith(yearPrefix))
      .reduce((sum, stat) => sum + getMetricValue(stat, goal.metricKey), 0);
  }

  if (goal.scope === "monthly") {
    const month = goal.month || new Date().getMonth() + 1;
    const monthKey = `${goal.year}-${String(month).padStart(2, "0")}`;
    const monthData = monthlyStats.find((stat) => stat.month === monthKey);
    return monthData ? getMetricValue(monthData, goal.metricKey) : 0;
  }

  return 0;
}

export function formatGoalValue(
  value: number,
  valueType: "currency" | "integer"
): string {
  if (valueType === "currency") {
    return formatCurrency(value, 0) + " DKK";
  }
  return formatInteger(value);
}

export function computeGoalProgress(
  goal: KpiGoal,
  kpiData: KpiReportResponse
): GoalProgress {
  const currentValue = getCurrentValueForGoal(goal, kpiData);
  const percentage =
    goal.targetValue > 0 ? (currentValue / goal.targetValue) * 100 : 0;

  return {
    goal,
    currentValue,
    percentage,
    formattedCurrent: formatGoalValue(currentValue, goal.valueType),
    formattedTarget: formatGoalValue(goal.targetValue, goal.valueType),
  };
}

export function computeAllGoalProgress(
  goals: KpiGoal[],
  kpiData: KpiReportResponse
): GoalProgress[] {
  return goals.map((goal) => computeGoalProgress(goal, kpiData));
}

export function prepareYearlyGoalTrendData(
  goal: KpiGoal,
  kpiData: KpiReportResponse
): Array<{
  month: string;
  monthLabel: string;
  value: number;
  cumulative: number;
}> {
  const yearPrefix = `${goal.year}-`;
  const yearMonths = kpiData.monthlyStats
    .filter((stat) => stat.month.startsWith(yearPrefix))
    .sort((a, b) => a.month.localeCompare(b.month));

  let cumulative = 0;
  return yearMonths.map((stat) => {
    const value = getMetricValue(stat, goal.metricKey);
    cumulative += value;
    const monthDate = new Date(stat.month + "-01");
    const monthLabel = monthDate.toLocaleString("en", { month: "short" });
    return { month: stat.month, monthLabel, value, cumulative };
  });
}
